import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONCERNS = [
  "Autism",
  "ADHD",
  "Speech Delay",
  "Global Developmental Delay",
  "Sensory or Behavioural Concerns",
  "Other",
  "Not Yet Diagnosed",
];

const inputCls =
  "w-full bg-[#FAF7F2] border border-[#E2DACD] rounded-xl px-3.5 py-2.5 text-sm text-[#2D3A32] placeholder:text-[#9aa79e] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059] transition";

export default function HeroLeadForm({ onStarted }) {
  const [form, setForm] = useState({ parent_name: "", phone: "", email: "", city: "", child_age: "", concern: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assessment-leads`, {
        parent_name: form.parent_name,
        whatsapp: form.phone,
        email: form.email,
        city_state: form.city,
        child_age: form.child_age,
        concern: form.concern,
        status: "Started",
      });
      trackEvent("HeroLeadSubmit", { concern: form.concern });
      onStarted({ id: res.data.id, ...form });
    } catch (err) {
      toast.error("Could not save your details. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="relative max-w-md mx-auto lg:max-w-none w-full"
    >
      <div
        data-testid="hero-lead-form"
        className="bg-white rounded-3xl border border-[#E5D3AD] shadow-[0_24px_60px_rgb(27,59,43,0.16)] p-6 sm:p-7"
      >
        <div className="flex items-center gap-3 pb-4 border-b border-[#E5D3AD]/60">
          <img
            src="/doctor.jpg"
            alt="Dr. Santhisree Bheesetti, B.A.M.S"
            className="w-11 h-11 rounded-full object-cover border border-[#C5A059]/50"
            style={{ objectPosition: "center 22%" }}
          />
          <div>
            <p className="text-sm font-semibold text-[#1B3B2B]">Dr. Santhisree Bheesetti, B.A.M.S</p>
            <p className="text-[11px] text-[#5C6E62]">Founder &amp; Chief Physician</p>
          </div>
        </div>

        <h3 className="mt-4 font-serif text-[1.45rem] leading-snug text-[#1B3B2B]">
          Start Your Child's Preliminary Assessment
        </h3>
        <p className="mt-1.5 text-[13px] text-[#5C6E62] leading-relaxed">
          Share a few details to begin understanding your child's individual pattern.
        </p>

        <form onSubmit={onSubmit} className="mt-4 space-y-2.5">
          <input data-testid="hero-lead-parent-name" required className={inputCls} placeholder="Parent Name *" value={form.parent_name} onChange={set("parent_name")} />
          <input data-testid="hero-lead-phone" required className={inputCls} placeholder="Phone / WhatsApp Number *" value={form.phone} onChange={set("phone")} />
          <input data-testid="hero-lead-email" required type="email" className={inputCls} placeholder="Email Address *" value={form.email} onChange={set("email")} />
          <div className="grid grid-cols-2 gap-2.5">
            <input data-testid="hero-lead-city" required className={inputCls} placeholder="City *" value={form.city} onChange={set("city")} />
            <input data-testid="hero-lead-age" required className={inputCls} placeholder="Child's Age *" value={form.child_age} onChange={set("child_age")} />
          </div>
          <select data-testid="hero-lead-concern-select" required className={inputCls} value={form.concern} onChange={set("concern")}>
            <option value="">Diagnosis / Primary Concern *</option>
            {CONCERNS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            data-testid="hero-lead-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#1B3B2B] text-[#FAF7F2] font-semibold tracking-wide text-[13px] px-6 py-3.5 rounded-full hover:bg-[#132C20] transition-all duration-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Start Free Assessment
          </button>
          <p className="text-[10px] text-[#8a968e] text-center leading-relaxed">
            Your details are used only to share your child's preliminary pattern and contact you regarding your request.
          </p>
        </form>
      </div>
    </motion.div>
  );
}
